import React, { useCallback, useState } from 'react';
import { useProgressBar } from 'react-aria';
import { useTheme } from 'styled-components';
import { invlerp } from 'utils/math';
import * as S from './styles';
import { ProgressBarProps } from './types';

const ProgressBar: React.FC<ProgressBarProps> = (props) => {
  const theme = useTheme();

  const {
    className,
    fillClassName,
    fillColor = theme.palette.actionPrimary.main,
    isIndeterminate: isIndeterminateProp,
    maxValue = 100,
    minValue = 0,
    trackColor = theme.palette.l5,
    trackClassName,
    value = 0,
  } = props;

  // a local copy of the isIndeterminate prop that allows the progress bar to
  // complete its current iteration before being replaced by the actuall progress bar
  const [isIndeterminate, setIsIndeterminate] = useState(isIndeterminateProp);
  const pctValue = invlerp(minValue, maxValue, value);
  const pct = `${Math.round(pctValue * 100)}%`;

  // if the props is true but the local value is false, it's safe to just sync
  // the local value immediately - it's `isIndterminateProp` being false that needs
  // special handling
  if (isIndeterminateProp && !isIndeterminate) {
    setIsIndeterminate(isIndeterminateProp);
  }

  const { progressBarProps } = useProgressBar({
    ...props,
    isIndeterminate,
    maxValue,
    minValue,
    value,
  });

  const handleIterationEnd = useCallback(() => {
    // when the indeterminate animation completes a loop, check if the progress bar
    // should still be indeterminate
    if (!isIndeterminateProp) {
      setIsIndeterminate(false);
    }
  }, [isIndeterminateProp]);

  return (
    <div className={className}>
      <S.ProgressBarTrack
        $color={trackColor}
        className={trackClassName}
        {...progressBarProps}
      >
        {!isIndeterminate ? (
          <S.ProgressBarFill
            $color={fillColor}
            className={fillClassName}
            style={{ transform: `translateX(${pct})` }}
          />
        ) : (
          <S.ProgressBarFillIndeterminate
            className={fillClassName}
            $color={fillColor}
            onAnimationIteration={handleIterationEnd}
          />
        )}
      </S.ProgressBarTrack>
    </div>
  );
};

export default ProgressBar;
